import { useToast } from "@chakra-ui/toast";
import {
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
} from "@mui/icons-material";
import { FC, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { NotificationTableDataRow_Mutation } from "~/src/__relay_artifacts__/NotificationTableDataRow_Mutation.graphql";
import { NotificationTableDataRow_notification$key } from "~/src/__relay_artifacts__/NotificationTableDataRow_notification.graphql";
import {
  TableDataColumn,
  TableDataRow,
} from "~/src/components/common/tables/Table";
import { TableActions } from "~/src/components/common/tables/TableActions";
import { DeleteConfirmDialog } from "~/src/components/features/global/DeleteConfirmDialog";
import { useOpenState } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";

import { NotificationColumn } from "./NotificationColumn";
import { ROW_HEIGHT } from "./styles";

type Props = {
  notificationRef: NotificationTableDataRow_notification$key;
  connectionId: string;
  withActions: boolean;
};

const fragment = graphql`
  fragment NotificationTableDataRow_notification on Notification {
    id
    slug
    title
    publishedAt
    category
    ...NotificationColumn_notification
  }
`;

const mutation = graphql`
  mutation NotificationTableDataRow_Mutation(
    $input: DeleteNotificationInput!
    $connections: [ID!]!
  ) {
    deleteNotification(input: $input) {
      deletedNotificationId @deleteEdge(connections: $connections)
    }
  }
`;

export const NotificationTableDataRow: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ notificationRef, connectionId, withActions }) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useOpenState();

  const notification = useFragment(fragment, notificationRef);
  const mutate = useMutationCommit<NotificationTableDataRow_Mutation>(mutation);

  const handleDelete = useCallback(async () => {
    try {
      const res = await mutate({
        variables: {
          input: {
            notificationId: notification.id,
          },
          connections: [connectionId],
        },
      });
      const deletedNotificationId =
        res.deleteNotification?.deletedNotificationId;
      if (!deletedNotificationId) throw new Error("assertion failed");

      toast({
        title: `${notification.title}を削除しました`,
        status: "success",
      });
    } catch (err) {
      toast({ title: "削除に失敗しました", status: "error" });
    }
  }, [mutate, notification.id, notification.title, connectionId, toast]);

  const actionMenus = useMemo(
    () => [
      {
        label: "編集",
        icon: <EditOutlinedIcon />,
        to: `/notifications/${notification.slug}/edit`,
      },
      {
        label: "削除",
        icon: <DeleteOutlineIcon />,
        onClick: () => onOpen(),
      },
    ],
    [notification.slug, onOpen]
  );

  return (
    <TableDataRow height="unset" minH={ROW_HEIGHT}>
      <TableDataColumn
        type="text"
        data={
          GraphQLEnums.NotificationCategory.find(
            (category) => category.value === notification.category
          )?.label
        }
      />
      <TableDataColumn
        type="actions"
        data={<NotificationColumn notificationRef={notification} />}
      />
      <TableDataColumn type="date" data={notification.publishedAt} />
      {withActions && (
        <TableDataColumn
          type="actions"
          data={
            <>
              <TableActions actionMenus={actionMenus} />
              <DeleteConfirmDialog
                name={notification.title}
                isOpen={isOpen}
                onClose={onClose}
                onDelete={handleDelete}
              />
            </>
          }
        />
      )}
    </TableDataRow>
  );
};
